import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../styles/design-system.js';
import { iconStyles } from '../styles/icon-styles.js';

export class CallHistory extends LitElement {
  static properties = {
    calls: { type: Array, state: true },
    filteredCalls: { type: Array, state: true },
    expandedRowId: { type: String, state: true },
    searchQuery: { type: String, state: true },
    activeStatusFilters: { type: Array, state: true },
    activeDateRange: { type: String, state: true },
    activeDirection: { type: String, state: true },
    sortBy: { type: String, state: true },
    sortDirection: { type: String, state: true },
    currentPage: { type: Number, state: true },
    pageSize: { type: Number, state: true },
  };

  static styles = [
    designSystem,
    componentStyles,
    css`
      :host {
        display: block;
        font-family: var(--font-body);
        color: var(--color-text-primary);
      }

      .page-layout {
        display: flex;
        flex-direction: column;
        gap: var(--space-8);
        animation: fadeIn var(--duration-slow) var(--ease-out) both;
      }

      /* ─── Search & Filter Row ─── */
      .filter-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: var(--space-3);
      }

      .search-wrapper {
        position: relative;
        flex: 1;
        min-width: 260px;
      }

      .search-wrapper .search-icon {
        position: absolute;
        left: var(--space-3);
        top: 50%;
        transform: translateY(-50%);
        color: var(--color-text-tertiary);
        font-size: var(--text-sm);
        pointer-events: none;
      }

      .search-wrapper input {
        width: 100%;
        padding: var(--space-2) var(--space-3) var(--space-2) var(--space-10);
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        color: var(--color-text-primary);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        line-height: var(--leading-normal);
        transition: all var(--transition-fast);
      }

      .search-wrapper input:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: var(--shadow-glow-primary);
      }

      .search-wrapper input::placeholder {
        color: var(--color-text-tertiary);
      }

      .filter-group {
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .filter-group-label {
        font-size: var(--text-xs);
        font-weight: var(--font-semibold);
        color: var(--color-text-tertiary);
        text-transform: uppercase;
        letter-spacing: var(--tracking-wider);
        white-space: nowrap;
      }

      .chip {
        display: inline-flex;
        align-items: center;
        gap: var(--space-1);
        padding: var(--space-1) var(--space-3);
        border-radius: var(--radius-full);
        font-family: var(--font-body);
        font-size: var(--text-xs);
        font-weight: var(--font-medium);
        cursor: pointer;
        transition: all var(--transition-base);
        border: 1px solid var(--color-border-primary);
        background: var(--color-bg-secondary);
        color: var(--color-text-secondary);
        white-space: nowrap;
        user-select: none;
      }

      .chip:hover {
        border-color: var(--color-border-secondary);
        background: var(--color-bg-tertiary);
      }

      .chip.active {
        background: var(--color-primary);
        color: var(--color-text-inverse);
        border-color: var(--color-primary);
      }

      .chip.active:hover {
        background: var(--color-primary-hover);
        border-color: var(--color-primary-hover);
      }

      /* ─── Active filter badges ─── */
      .active-filters {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-2);
      }

      .filter-badge {
        display: inline-flex;
        align-items: center;
        gap: var(--space-1);
        padding: 2px var(--space-2) 2px var(--space-3);
        background: var(--color-primary-subtle);
        color: var(--color-primary);
        border-radius: var(--radius-full);
        font-size: var(--text-xs);
        font-weight: var(--font-semibold);
      }

      .filter-badge .dismiss {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        border-radius: var(--radius-full);
        background: transparent;
        border: none;
        color: var(--color-primary);
        cursor: pointer;
        font-size: 10px;
        transition: background var(--transition-fast);
        padding: 0;
      }

      .filter-badge .dismiss:hover {
        background: var(--color-primary-light);
      }

      /* ─── Stats Summary Bar ─── */
      .stats-bar {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: var(--space-3);
      }

      .mini-stat {
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-xl);
        padding: var(--space-6);
        display: flex;
        align-items: center;
        gap: var(--space-3);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        transition: all var(--transition-base);
        position: relative;
        overflow: hidden;
      }

      .mini-stat:hover {
        box-shadow: var(--shadow-md);
        border-color: var(--color-border-secondary);
      }

      .mini-stat-icon {
        width: 36px;
        height: 36px;
        border-radius: var(--radius-md);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--text-base);
        flex-shrink: 0;
      }

      .mini-stat-icon.calls {
        background: var(--color-primary-light);
        color: var(--color-primary);
      }
      .mini-stat-icon.completed {
        background: var(--color-success-light);
        color: var(--color-success);
      }
      .mini-stat-icon.duration {
        background: var(--color-accent-light);
        color: var(--color-accent);
      }
      .mini-stat-icon.minutes {
        background: var(--color-info-light);
        color: var(--color-info);
      }

      .mini-stat-content {
        display: flex;
        flex-direction: column;
        gap: 1px;
        min-width: 0;
      }

      .mini-stat-value {
        font-family: var(--font-display);
        font-size: var(--text-xl);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        line-height: var(--leading-tight);
        letter-spacing: var(--tracking-tight);
      }

      .mini-stat-label {
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        font-weight: var(--font-medium);
        text-transform: uppercase;
        letter-spacing: var(--tracking-wider);
      }

      /* ─── Table Card ─── */
      .table-card {
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border: 1px solid rgba(255, 255, 255, 0.35);
        border-radius: var(--radius-xl);
        overflow: hidden;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
      }

      .table-scroll {
        overflow-x: auto;
      }

      table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        font-family: var(--font-body);
      }

      thead th {
        padding: var(--space-3) var(--space-4);
        text-align: left;
        font-size: var(--text-xs);
        font-weight: var(--font-semibold);
        color: var(--color-text-tertiary);
        text-transform: uppercase;
        letter-spacing: var(--tracking-wider);
        border-bottom: 1px solid var(--color-border-primary);
        background: var(--color-bg-tertiary);
        white-space: nowrap;
        user-select: none;
        position: sticky;
        top: 0;
        z-index: 2;
      }

      thead th.sortable {
        cursor: pointer;
        transition: color var(--transition-fast);
      }

      thead th.sortable:hover {
        color: var(--color-text-primary);
      }

      .sort-arrows {
        display: inline-flex;
        flex-direction: column;
        margin-left: var(--space-1);
        font-size: 8px;
        line-height: 1;
        vertical-align: middle;
        color: var(--color-text-disabled);
      }

      .sort-arrows .up,
      .sort-arrows .down {
        transition: color var(--transition-fast);
      }

      .sort-arrows .up.active,
      .sort-arrows .down.active {
        color: var(--color-primary);
      }

      tbody tr {
        transition: background var(--transition-fast);
        cursor: pointer;
      }

      tbody tr:nth-child(even) {
        background: rgba(245, 245, 244, 0.4);
      }

      tbody tr:hover {
        background: var(--color-bg-hover);
      }

      tbody tr.expanded-parent {
        background: var(--color-primary-light);
      }

      tbody td {
        padding: var(--space-3) var(--space-4);
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
        border-bottom: 1px solid var(--color-border-subtle);
        vertical-align: middle;
      }

      .cell-datetime {
        display: flex;
        flex-direction: column;
        gap: 1px;
      }

      .cell-datetime .date {
        font-weight: var(--font-medium);
        color: var(--color-text-primary);
        font-size: var(--text-sm);
      }

      .cell-datetime .time {
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }

      .cell-phone {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-primary);
        letter-spacing: 0.02em;
      }

      .cell-agent {
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .agent-avatar {
        width: 24px;
        height: 24px;
        border-radius: var(--radius-full);
        background: var(--color-primary-subtle);
        color: var(--color-primary);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 10px;
        flex-shrink: 0;
      }

      .cell-duration {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        color: var(--color-text-primary);
      }

      .cell-direction {
        display: flex;
        align-items: center;
        gap: var(--space-1);
        font-size: var(--text-xs);
        font-weight: var(--font-medium);
      }

      .cell-direction.inbound {
        color: var(--color-accent);
      }

      .cell-direction.outbound {
        color: var(--color-info);
      }

      .cell-direction i {
        font-size: var(--text-sm);
      }

      .cell-sentiment {
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .sentiment-bar-bg {
        width: 48px;
        height: 6px;
        background: var(--color-bg-inset);
        border-radius: var(--radius-full);
        overflow: hidden;
      }

      .sentiment-bar-fill {
        height: 100%;
        border-radius: var(--radius-full);
        transition: width var(--transition-base);
      }

      .sentiment-bar-fill.positive {
        background: var(--color-success);
      }
      .sentiment-bar-fill.neutral {
        background: var(--color-warning);
      }
      .sentiment-bar-fill.negative {
        background: var(--color-error);
      }

      .sentiment-label {
        font-size: var(--text-xs);
        font-weight: var(--font-medium);
      }

      .sentiment-label.positive { color: var(--color-success-text); }
      .sentiment-label.neutral { color: var(--color-warning-text); }
      .sentiment-label.negative { color: var(--color-error-text); }

      .cell-actions {
        display: flex;
        align-items: center;
        gap: var(--space-1);
      }

      .action-btn {
        width: 28px;
        height: 28px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: none;
        background: transparent;
        color: var(--color-text-tertiary);
        border-radius: var(--radius-sm);
        cursor: pointer;
        transition: all var(--transition-fast);
        font-size: var(--text-sm);
        padding: 0;
      }

      .action-btn:hover {
        background: var(--color-bg-tertiary);
        color: var(--color-primary);
      }

      /* ─── Expanded Row Detail ─── */
      .expanded-detail {
        background: var(--color-bg-tertiary);
        border-bottom: 1px solid var(--color-border-primary);
        box-shadow: var(--shadow-xs);
      }

      .expanded-detail td {
        padding: 0;
      }

      .detail-inner {
        padding: var(--space-6) var(--space-8);
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: var(--space-8);
        animation: slideDown var(--duration-base) var(--ease-out) both;
      }

      .detail-section {
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
      }

      .detail-section-title {
        font-family: var(--font-display);
        font-size: var(--text-sm);
        font-weight: var(--font-semibold);
        color: var(--color-text-primary);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .detail-section-title i {
        color: var(--color-primary);
        font-size: var(--text-sm);
      }

      .ai-summary {
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
        line-height: var(--leading-relaxed);
        background: rgba(255, 255, 255, 0.18);
        backdrop-filter: blur(8px) saturate(1.2);
        -webkit-backdrop-filter: blur(8px) saturate(1.2);
        border-radius: var(--radius-md);
        padding: var(--space-3);
        border: 1px solid rgba(255, 255, 255, 0.35);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5), inset 0 -1px 0 rgba(255, 255, 255, 0.08);
        position: relative;
        overflow: hidden;
      }

      .sentiment-detail {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }

      .sentiment-score-bar {
        flex: 1;
        height: 8px;
        background: var(--color-bg-inset);
        border-radius: var(--radius-full);
        overflow: hidden;
      }

      .sentiment-score-fill {
        height: 100%;
        border-radius: var(--radius-full);
        transition: width var(--transition-slow);
      }

      .sentiment-score-value {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        font-weight: var(--font-semibold);
        min-width: 38px;
        text-align: right;
      }

      .topics-list {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-2);
      }

      .topic-tag {
        padding: 2px var(--space-2);
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-full);
        font-size: var(--text-xs);
        font-weight: var(--font-medium);
        color: var(--color-text-secondary);
      }

      .transcript-btn {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2) var(--space-4);
        background: var(--color-primary);
        color: var(--color-text-inverse);
        border: none;
        border-radius: var(--radius-md);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        cursor: pointer;
        transition: all var(--transition-fast);
      }

      .transcript-btn:hover {
        background: var(--color-primary-hover);
        box-shadow: var(--shadow-md);
        transform: translateY(-1px);
      }

      /* ─── Pagination ─── */
      .pagination-bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-3) var(--space-4);
        border-top: 1px solid var(--color-border-primary);
        background: var(--color-bg-tertiary);
      }

      .pagination-info {
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
      }

      .pagination-info strong {
        color: var(--color-text-secondary);
        font-weight: var(--font-semibold);
      }

      .pagination-controls {
        display: flex;
        align-items: center;
        gap: var(--space-1);
      }

      .page-btn {
        min-width: 32px;
        height: 32px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0 var(--space-2);
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
      }

      .page-btn:hover:not(:disabled) {
        background: var(--color-bg-tertiary);
        border-color: var(--color-border-secondary);
        color: var(--color-text-primary);
      }

      .page-btn.active {
        background: var(--color-primary);
        border-color: var(--color-primary);
        color: var(--color-text-inverse);
      }

      .page-btn:disabled {
        opacity: 0.35;
        cursor: not-allowed;
      }

      .page-ellipsis {
        min-width: 32px;
        height: 32px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: var(--color-text-tertiary);
        font-size: var(--text-sm);
      }

      /* ─── Empty State ─── */
      .empty-row td {
        padding: var(--space-16) var(--space-4);
        text-align: center;
      }

      .empty-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--space-2);
      }

      .empty-icon {
        font-size: 2.5rem;
        color: var(--color-text-disabled);
        margin-bottom: var(--space-2);
      }

      .empty-title {
        font-family: var(--font-display);
        font-size: var(--text-lg);
        font-weight: var(--font-semibold);
        color: var(--color-text-secondary);
      }

      .empty-desc {
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
      }

      /* ─── Responsive ─── */
      @media (max-width: 900px) {
        .stats-bar {
          grid-template-columns: repeat(2, 1fr);
        }
        .detail-inner {
          grid-template-columns: 1fr;
        }
      }

      @media (max-width: 640px) {
        .stats-bar {
          grid-template-columns: 1fr;
        }
        .filter-row {
          flex-direction: column;
          align-items: stretch;
        }
        .search-wrapper {
          min-width: auto;
        }
      }
    `
  ];

  constructor() {
    super();
    this.calls = [];
    this.filteredCalls = [];
    this.expandedRowId = null;
    this.searchQuery = '';
    this.activeStatusFilters = [];
    this.activeDateRange = 'all';
    this.activeDirection = 'all';
    this.sortBy = 'dateTime';
    this.sortDirection = 'desc';
    this.currentPage = 1;
    this.pageSize = 20;

    this._generateMockData();
    this._applyFilters();
  }

  /* ───────────────────────────────────────────
     Mock Data
  ─────────────────────────────────────────── */
  _generateMockData() {
    const agents = ['Ava (AI)', 'Noah (AI)', 'Mia (AI)', 'Liam (AI)'];
    const statuses = ['completed', 'missed', 'abandoned', 'transferred'];
    const directions = ['inbound', 'outbound'];
    const sentiments = ['positive', 'neutral', 'negative'];
    const topicsPool = [
      'Reservation', 'Pricing', 'Availability', 'Cancellation',
      'Refund', 'Hours', 'Location', 'Menu', 'Special Event',
      'Complaint', 'Feedback', 'Waitlist', 'Party Size', 'Allergy Info'
    ];
    const summaries = [
      'Caller inquired about table availability for a party of 6 on Friday evening. Reservation was successfully created for 7:30 PM.',
      'Customer called to cancel their existing reservation. Cancellation was processed and confirmation sent via SMS.',
      'Inquiry about private dining options for a corporate event of 20 guests. Details forwarded to events team.',
      'Caller asked about menu options for dietary restrictions. Provided allergy-friendly alternatives and emailed the full menu.',
      'Returning customer requested modification to existing reservation. Updated party size from 4 to 6 and confirmed.',
      'New customer asked about pricing for weekend brunch. Provided details and created a reservation for Sunday.',
      'Caller reported issue with a previous visit. Escalated to manager and offered complimentary return visit.',
      'Customer wanted to confirm operating hours for the holiday weekend. Provided updated schedule information.',
    ];

    const calls = [];
    for (let i = 0; i < 100; i++) {
      const daysAgo = Math.random() * 35;
      const date = new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000);
      const status = statuses[Math.floor(Math.random() * statuses.length)];
      const direction = directions[Math.floor(Math.random() * directions.length)];
      const sentiment = sentiments[Math.floor(Math.random() * sentiments.length)];
      const sentimentScore = sentiment === 'positive'
        ? 0.65 + Math.random() * 0.35
        : sentiment === 'neutral'
          ? 0.35 + Math.random() * 0.3
          : Math.random() * 0.35;
      const duration = status === 'missed' ? 0 : Math.floor(Math.random() * 720) + 15;
      const numTopics = Math.floor(Math.random() * 4) + 1;
      const shuffled = [...topicsPool].sort(() => 0.5 - Math.random());
      const topics = shuffled.slice(0, numTopics);

      calls.push({
        id: `call-${String(i).padStart(4, '0')}`,
        dateTime: date,
        phone: `+1 (${Math.floor(Math.random() * 900) + 100}) ${Math.floor(Math.random() * 900) + 100}-${Math.floor(Math.random() * 9000) + 1000}`,
        agent: agents[Math.floor(Math.random() * agents.length)],
        duration,
        direction,
        status,
        sentiment,
        sentimentScore: Math.round(sentimentScore * 100) / 100,
        topics,
        summary: summaries[Math.floor(Math.random() * summaries.length)],
      });
    }

    this.calls = calls;
  }

  /* ───────────────────────────────────────────
     Filter / Sort Logic
  ─────────────────────────────────────────── */
  _applyFilters() {
    let result = [...this.calls];

    // Search
    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase();
      result = result.filter(c =>
        c.phone.toLowerCase().includes(q) ||
        c.agent.toLowerCase().includes(q) ||
        c.id.toLowerCase().includes(q)
      );
    }

    // Status
    if (this.activeStatusFilters.length > 0) {
      result = result.filter(c => this.activeStatusFilters.includes(c.status));
    }

    // Direction
    if (this.activeDirection !== 'all') {
      result = result.filter(c => c.direction === this.activeDirection);
    }

    // Date range
    if (this.activeDateRange !== 'all') {
      const now = new Date();
      let cutoff;
      switch (this.activeDateRange) {
        case 'today': {
          cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        }
        case '7d': {
          cutoff = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        }
        case '30d': {
          cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        }
        default:
          cutoff = null;
      }
      if (cutoff) {
        result = result.filter(c => c.dateTime >= cutoff);
      }
    }

    // Sort
    result.sort((a, b) => {
      let aVal, bVal;
      switch (this.sortBy) {
        case 'dateTime': aVal = a.dateTime.getTime(); bVal = b.dateTime.getTime(); break;
        case 'phone': aVal = a.phone; bVal = b.phone; break;
        case 'agent': aVal = a.agent; bVal = b.agent; break;
        case 'duration': aVal = a.duration; bVal = b.duration; break;
        case 'status': aVal = a.status; bVal = b.status; break;
        case 'sentiment': aVal = a.sentimentScore; bVal = b.sentimentScore; break;
        default: aVal = a.dateTime.getTime(); bVal = b.dateTime.getTime();
      }
      if (typeof aVal === 'string') {
        const cmp = aVal.localeCompare(bVal);
        return this.sortDirection === 'asc' ? cmp : -cmp;
      }
      return this.sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });

    this.filteredCalls = result;
    const maxPage = Math.max(1, Math.ceil(result.length / this.pageSize));
    if (this.currentPage > maxPage) this.currentPage = maxPage;
  }

  /* ───────────────────────────────────────────
     Handlers
  ─────────────────────────────────────────── */
  _onSearch(e) {
    this.searchQuery = e.target.value;
    this.currentPage = 1;
    this._applyFilters();
  }

  _toggleStatusFilter(status) {
    const idx = this.activeStatusFilters.indexOf(status);
    if (idx >= 0) {
      this.activeStatusFilters = this.activeStatusFilters.filter(s => s !== status);
    } else {
      this.activeStatusFilters = [...this.activeStatusFilters, status];
    }
    this.currentPage = 1;
    this._applyFilters();
  }

  _setDateRange(range) {
    this.activeDateRange = this.activeDateRange === range ? 'all' : range;
    this.currentPage = 1;
    this._applyFilters();
  }

  _setDirection(dir) {
    this.activeDirection = this.activeDirection === dir ? 'all' : dir;
    this.currentPage = 1;
    this._applyFilters();
  }

  _dismissFilter(type, value) {
    switch (type) {
      case 'status':
        this.activeStatusFilters = this.activeStatusFilters.filter(s => s !== value);
        break;
      case 'date':
        this.activeDateRange = 'all';
        break;
      case 'direction':
        this.activeDirection = 'all';
        break;
    }
    this.currentPage = 1;
    this._applyFilters();
  }

  _handleSort(column) {
    if (this.sortBy === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = column;
      this.sortDirection = 'desc';
    }
    this._applyFilters();
  }

  _toggleRow(id) {
    this.expandedRowId = this.expandedRowId === id ? null : id;
  }

  _goToPage(p) {
    const max = Math.max(1, Math.ceil(this.filteredCalls.length / this.pageSize));
    this.currentPage = Math.max(1, Math.min(p, max));
  }

  /* ───────────────────────────────────────────
     Helpers
  ─────────────────────────────────────────── */
  _fmtDuration(sec) {
    if (sec === 0) return '--:--';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }

  _fmtDate(d) {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  _fmtTime(d) {
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  _statusBadgeClass(status) {
    const map = { completed: 'badge-success', missed: 'badge-error', abandoned: 'badge-warning', transferred: 'badge-info' };
    return map[status] || 'badge-neutral';
  }

  _sentimentClass(s) {
    if (s === 'positive') return 'positive';
    if (s === 'negative') return 'negative';
    return 'neutral';
  }

  _sentimentColor(s) {
    if (s === 'positive') return 'var(--color-success)';
    if (s === 'negative') return 'var(--color-error)';
    return 'var(--color-warning)';
  }

  /* ───────────────────────────────────────────
     Computed
  ─────────────────────────────────────────── */
  get _pageData() {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredCalls.slice(start, start + this.pageSize);
  }

  get _totalPages() {
    return Math.max(1, Math.ceil(this.filteredCalls.length / this.pageSize));
  }

  get _stats() {
    const total = this.filteredCalls.length;
    const completed = this.filteredCalls.filter(c => c.status === 'completed').length;
    const completedPct = total > 0 ? Math.round((completed / total) * 100) : 0;
    const totalSec = this.filteredCalls.reduce((s, c) => s + c.duration, 0);
    const avgSec = total > 0 ? Math.round(totalSec / total) : 0;
    const totalMin = Math.round(totalSec / 60);
    return { total, completedPct, avgDuration: this._fmtDuration(avgSec), totalMin };
  }

  get _hasActiveFilters() {
    return this.activeStatusFilters.length > 0 || this.activeDateRange !== 'all' || this.activeDirection !== 'all';
  }

  /* ───────────────────────────────────────────
     Render
  ─────────────────────────────────────────── */
  render() {
    const stats = this._stats;
    const startIdx = (this.currentPage - 1) * this.pageSize;
    const endIdx = Math.min(startIdx + this.pageSize, this.filteredCalls.length);

    return html`
      ${iconStyles}
      <div class="page-layout">

        <!-- Search & Filter Row -->
        <div class="filter-row">
          <div class="search-wrapper">
            <i class="fa-sharp fa-light fa-magnifying-glass search-icon"></i>
            <input
              type="text"
              placeholder="Search calls by phone, agent, or ID..."
              .value="${this.searchQuery}"
              @input="${this._onSearch}"
            />
          </div>

          <div class="filter-group">
            <span class="filter-group-label">Status</span>
            ${['completed', 'missed', 'abandoned', 'transferred'].map(s => html`
              <span
                class="chip ${this.activeStatusFilters.includes(s) ? 'active' : ''}"
                @click="${() => this._toggleStatusFilter(s)}"
              >${s.charAt(0).toUpperCase() + s.slice(1)}</span>
            `)}
          </div>

          <div class="filter-group">
            <span class="filter-group-label">Range</span>
            ${[['today', 'Today'], ['7d', '7D'], ['30d', '30D'], ['all', 'All']].map(([v, l]) => html`
              <span
                class="chip ${this.activeDateRange === v ? 'active' : ''}"
                @click="${() => { this.activeDateRange = v; this.currentPage = 1; this._applyFilters(); }}"
              >${l}</span>
            `)}
          </div>

          <div class="filter-group">
            <span class="filter-group-label">Direction</span>
            ${[['inbound', 'Inbound'], ['outbound', 'Outbound']].map(([v, l]) => html`
              <span
                class="chip ${this.activeDirection === v ? 'active' : ''}"
                @click="${() => this._setDirection(v)}"
              >${l}</span>
            `)}
          </div>
        </div>

        <!-- Active Filter Badges -->
        ${this._hasActiveFilters ? html`
          <div class="active-filters">
            ${this.activeStatusFilters.map(s => html`
              <span class="filter-badge">
                ${s.charAt(0).toUpperCase() + s.slice(1)}
                <button class="dismiss" @click="${() => this._dismissFilter('status', s)}">
                  <i class="fa-sharp fa-light fa-xmark"></i>
                </button>
              </span>
            `)}
            ${this.activeDateRange !== 'all' ? html`
              <span class="filter-badge">
                ${this.activeDateRange === 'today' ? 'Today' : this.activeDateRange === '7d' ? 'Last 7 days' : 'Last 30 days'}
                <button class="dismiss" @click="${() => this._dismissFilter('date')}">
                  <i class="fa-sharp fa-light fa-xmark"></i>
                </button>
              </span>
            ` : ''}
            ${this.activeDirection !== 'all' ? html`
              <span class="filter-badge">
                ${this.activeDirection === 'inbound' ? 'Inbound' : 'Outbound'}
                <button class="dismiss" @click="${() => this._dismissFilter('direction')}">
                  <i class="fa-sharp fa-light fa-xmark"></i>
                </button>
              </span>
            ` : ''}
          </div>
        ` : ''}

        <!-- Stats Summary Bar -->
        <div class="stats-bar">
          <div class="mini-stat">
            <div class="mini-stat-icon calls">
              <i class="fa-sharp-duotone fa-solid fa-phone"></i>
            </div>
            <div class="mini-stat-content">
              <span class="mini-stat-value">${stats.total.toLocaleString()}</span>
              <span class="mini-stat-label">Total Calls</span>
            </div>
          </div>
          <div class="mini-stat">
            <div class="mini-stat-icon completed">
              <i class="fa-sharp-duotone fa-solid fa-circle-check"></i>
            </div>
            <div class="mini-stat-content">
              <span class="mini-stat-value">${stats.completedPct}%</span>
              <span class="mini-stat-label">Completed</span>
            </div>
          </div>
          <div class="mini-stat">
            <div class="mini-stat-icon duration">
              <i class="fa-sharp-duotone fa-solid fa-stopwatch"></i>
            </div>
            <div class="mini-stat-content">
              <span class="mini-stat-value">${stats.avgDuration}</span>
              <span class="mini-stat-label">Avg Duration</span>
            </div>
          </div>
          <div class="mini-stat">
            <div class="mini-stat-icon minutes">
              <i class="fa-sharp-duotone fa-solid fa-clock"></i>
            </div>
            <div class="mini-stat-content">
              <span class="mini-stat-value">${stats.totalMin.toLocaleString()}</span>
              <span class="mini-stat-label">Total Minutes</span>
            </div>
          </div>
        </div>

        <!-- Data Table -->
        <div class="table-card">
          <div class="table-scroll">
            <table>
              <thead>
                <tr>
                  ${this._renderTh('Date / Time', 'dateTime')}
                  ${this._renderTh('Phone Number', 'phone')}
                  ${this._renderTh('Agent', 'agent')}
                  ${this._renderTh('Duration', 'duration')}
                  <th>Direction</th>
                  ${this._renderTh('Status', 'status')}
                  ${this._renderTh('Sentiment', 'sentiment')}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                ${this._pageData.length > 0
                  ? this._pageData.map(call => this._renderCallRow(call))
                  : html`
                    <tr class="empty-row">
                      <td colspan="8">
                        <div class="empty-content">
                          <i class="fa-sharp fa-light fa-rectangle-history-circle-plus empty-icon"></i>
                          <div class="empty-title">No calls found</div>
                          <div class="empty-desc">Try adjusting your search or filter criteria.</div>
                        </div>
                      </td>
                    </tr>
                  `
                }
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          ${this.filteredCalls.length > 0 ? html`
            <div class="pagination-bar">
              <div class="pagination-info">
                Showing <strong>${startIdx + 1}-${endIdx}</strong> of <strong>${this.filteredCalls.length.toLocaleString()}</strong>
              </div>
              <div class="pagination-controls">
                <button class="page-btn" ?disabled="${this.currentPage <= 1}" @click="${() => this._goToPage(this.currentPage - 1)}">
                  <i class="fa-sharp fa-light fa-chevron-left"></i>
                </button>
                ${this._renderPageNumbers()}
                <button class="page-btn" ?disabled="${this.currentPage >= this._totalPages}" @click="${() => this._goToPage(this.currentPage + 1)}">
                  <i class="fa-sharp fa-light fa-chevron-right"></i>
                </button>
              </div>
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }

  _renderTh(label, column) {
    const isActive = this.sortBy === column;
    return html`
      <th class="sortable" @click="${() => this._handleSort(column)}">
        ${label}
        <span class="sort-arrows">
          <span class="up ${isActive && this.sortDirection === 'asc' ? 'active' : ''}">&#9650;</span>
          <span class="down ${isActive && this.sortDirection === 'desc' ? 'active' : ''}">&#9660;</span>
        </span>
      </th>
    `;
  }

  _renderCallRow(call) {
    const isExpanded = this.expandedRowId === call.id;
    const sClass = this._sentimentClass(call.sentiment);
    const pct = Math.round(call.sentimentScore * 100);

    return html`
      <tr
        class="${isExpanded ? 'expanded-parent' : ''}"
        @click="${() => this._toggleRow(call.id)}"
      >
        <td>
          <div class="cell-datetime">
            <span class="date">${this._fmtDate(call.dateTime)}</span>
            <span class="time">${this._fmtTime(call.dateTime)}</span>
          </div>
        </td>
        <td><span class="cell-phone">${call.phone}</span></td>
        <td>
          <div class="cell-agent">
            <span class="agent-avatar"><i class="fa-sharp fa-light fa-robot"></i></span>
            ${call.agent}
          </div>
        </td>
        <td><span class="cell-duration">${this._fmtDuration(call.duration)}</span></td>
        <td>
          <span class="cell-direction ${call.direction}">
            <i class="fa-sharp fa-light ${call.direction === 'inbound' ? 'fa-arrow-down-left' : 'fa-arrow-up-right'}"></i>
            ${call.direction === 'inbound' ? 'In' : 'Out'}
          </span>
        </td>
        <td><span class="badge ${this._statusBadgeClass(call.status)}">${call.status}</span></td>
        <td>
          <div class="cell-sentiment">
            <div class="sentiment-bar-bg">
              <div class="sentiment-bar-fill ${sClass}" style="width: ${pct}%"></div>
            </div>
            <span class="sentiment-label ${sClass}">${call.sentiment}</span>
          </div>
        </td>
        <td>
          <div class="cell-actions" @click="${(e) => e.stopPropagation()}">
            <button class="action-btn" title="Play recording">
              <i class="fa-sharp fa-light fa-play"></i>
            </button>
            <button class="action-btn" title="View transcript">
              <i class="fa-sharp fa-light fa-file-lines"></i>
            </button>
            <button class="action-btn" title="Download">
              <i class="fa-sharp fa-light fa-arrow-down-to-bracket"></i>
            </button>
          </div>
        </td>
      </tr>
      ${isExpanded ? html`
        <tr class="expanded-detail">
          <td colspan="8">
            <div class="detail-inner">
              <div class="detail-section">
                <div class="detail-section-title">
                  <i class="fa-sharp fa-light fa-sparkles"></i>
                  AI Summary
                </div>
                <div class="ai-summary">${call.summary}</div>

                <div class="detail-section-title" style="margin-top: var(--space-2);">
                  <i class="fa-sharp fa-light fa-tags"></i>
                  Key Topics
                </div>
                <div class="topics-list">
                  ${call.topics.map(t => html`<span class="topic-tag">${t}</span>`)}
                </div>
              </div>

              <div class="detail-section">
                <div class="detail-section-title">
                  <i class="fa-sharp fa-light fa-face-smile"></i>
                  Sentiment Score
                </div>
                <div class="sentiment-detail">
                  <div class="sentiment-score-bar">
                    <div
                      class="sentiment-score-fill"
                      style="width: ${pct}%; background: ${this._sentimentColor(call.sentiment)}"
                    ></div>
                  </div>
                  <span class="sentiment-score-value" style="color: ${this._sentimentColor(call.sentiment)}">
                    ${pct}%
                  </span>
                </div>

                <button class="transcript-btn" style="margin-top: var(--space-3);">
                  <i class="fa-sharp fa-light fa-file-lines"></i>
                  View Full Transcript
                </button>
              </div>
            </div>
          </td>
        </tr>
      ` : ''}
    `;
  }

  _renderPageNumbers() {
    const total = this._totalPages;
    const current = this.currentPage;
    const pages = [];
    const range = 2;

    // Always show first page
    pages.push(1);

    // Ellipsis after first if needed
    if (current - range > 2) {
      pages.push('...');
    }

    // Pages around current
    for (let i = Math.max(2, current - range); i <= Math.min(total - 1, current + range); i++) {
      pages.push(i);
    }

    // Ellipsis before last if needed
    if (current + range < total - 1) {
      pages.push('...');
    }

    // Always show last page if > 1
    if (total > 1) {
      pages.push(total);
    }

    return pages.map(p =>
      p === '...'
        ? html`<span class="page-ellipsis">...</span>`
        : html`
          <button
            class="page-btn ${p === current ? 'active' : ''}"
            @click="${() => this._goToPage(p)}"
          >${p}</button>
        `
    );
  }
}

customElements.define('call-history', CallHistory);
